import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import ProjectsExportDialog from "../components/ProjectsExportDialog";
import MemberWorkReportDialog from "../components/MemberWorkReportDialog";
import TeamComparisonDialog from "../components/TeamComparisonDialog";
import { FilePdf, FileText, Receipt, UserCircle, Users } from "@phosphor-icons/react";

export default function ReportsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [memberReportOpen, setMemberReportOpen] = useState(false);
  const [teamComparisonOpen, setTeamComparisonOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await http.get("/projects");
        setProjects(r.data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const civilCount = projects.filter((p) => (p.category || "civil") === "civil").length;
  const interiorCount = projects.filter((p) => p.category === "interior").length;
  const activeCount = projects.filter((p) => (p.status || "active") === "active").length;

  return (
    <Layout
      title="Reports"
      subtitle="Export operational data as print-ready PDFs. All reports open in a new tab for download."
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Projects report card */}
        <div
          data-testid="report-card-projects"
          className="bg-white border-2 border-zinc-900 shadow-brutal p-6"
        >
          <div className="flex items-start gap-3">
            <div className="bg-amber-100 p-3 border-2 border-amber-700">
              <FilePdf size={28} weight="duotone" className="text-amber-800" />
            </div>
            <div className="flex-1">
              <div className="label-overline text-amber-700">Flagship Report</div>
              <h3 className="font-display font-black text-2xl tracking-tighter mt-1">
                Projects Export
              </h3>
              <p className="text-sm text-zinc-600 mt-1">
                Pick any Civil / Interior projects. Choose which columns to print
                (Job No., client, dates, fees, dues). Add a totals row at the bottom.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-5" data-testid="reports-stats">
            <Stat label="Civil" value={civilCount} />
            <Stat label="Interior" value={interiorCount} />
            <Stat label="Active" value={activeCount} />
          </div>

          <Button
            data-testid="open-projects-export"
            onClick={() => setExportOpen(true)}
            disabled={loading}
            className="w-full mt-5 rounded-none h-11 bg-amber-700 hover:bg-amber-800 text-white font-mono uppercase tracking-widest text-xs"
          >
            <FilePdf size={14} weight="bold" className="mr-2" />
            {loading ? "Loading projects…" : "Open Projects Export"}
          </Button>
        </div>

        {/* Member Work Report — real tile */}
        <div
          data-testid="report-card-member"
          className="bg-white border-2 border-zinc-900 shadow-brutal p-6"
        >
          <div className="flex items-start gap-3">
            <div className="bg-emerald-100 p-3 border-2 border-emerald-700">
              <UserCircle size={28} weight="duotone" className="text-emerald-800" />
            </div>
            <div className="flex-1">
              <div className="label-overline text-emerald-700">Team Performance</div>
              <h3 className="font-display font-black text-2xl tracking-tighter mt-1">
                Member Work Report
              </h3>
              <p className="text-sm text-zinc-600 mt-1">
                Pick any team member or project manager, choose a month or custom
                date range, and print every task they handled — with status,
                milestones and completed-on dates.
              </p>
            </div>
          </div>
          <Button
            data-testid="open-member-report"
            onClick={() => setMemberReportOpen(true)}
            className="w-full mt-5 rounded-none h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-mono uppercase tracking-widest text-xs"
          >
            <UserCircle size={14} weight="bold" className="mr-2" />
            Open Member Report
          </Button>
        </div>

        {/* Team Comparison / Studio Summary — real tile */}
        <div
          data-testid="report-card-team"
          className="bg-white border-2 border-zinc-900 shadow-brutal p-6"
        >
          <div className="flex items-start gap-3">
            <div className="bg-indigo-100 p-3 border-2 border-indigo-700">
              <Users size={28} weight="duotone" className="text-indigo-800" />
            </div>
            <div className="flex-1">
              <div className="label-overline text-indigo-700">Studio Summary</div>
              <h3 className="font-display font-black text-2xl tracking-tighter mt-1">
                Team Comparison
              </h3>
              <p className="text-sm text-zinc-600 mt-1">
                One-page side-by-side leaderboard for the month — total tasks,
                completed, in-progress, milestones delivered and completion-%
                for every member and manager.
              </p>
            </div>
          </div>
          <Button
            data-testid="open-team-comparison"
            onClick={() => setTeamComparisonOpen(true)}
            className="w-full mt-5 rounded-none h-11 bg-indigo-700 hover:bg-indigo-800 text-white font-mono uppercase tracking-widest text-xs"
          >
            <Users size={14} weight="bold" className="mr-2" />
            Open Studio Summary
          </Button>
        </div>

        {/* Placeholder tiles for future reports — keeps the page from feeling empty */}
        <PlaceholderCard
          icon={<Receipt size={28} weight="duotone" className="text-zinc-500" />}
          title="Invoices Report"
          hint="Coming soon — export billed, paid & overdue invoices in one printable sheet."
        />
        <PlaceholderCard
          icon={<FileText size={28} weight="duotone" className="text-zinc-500" />}
          title="Advances Ledger"
          hint="Coming soon — bank-ready advance-deposit statement grouped by project."
        />
      </div>

      <ProjectsExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        projects={projects}
      />
      <MemberWorkReportDialog
        open={memberReportOpen}
        onOpenChange={setMemberReportOpen}
      />
      <TeamComparisonDialog
        open={teamComparisonOpen}
        onOpenChange={setTeamComparisonOpen}
      />
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-200 bg-zinc-50 p-3">
      <div className="label-overline text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl tracking-tighter">{value}</div>
    </div>
  );
}

function PlaceholderCard({ icon, title, hint }) {
  return (
    <div className="bg-white border border-dashed border-zinc-300 p-6 opacity-80">
      <div className="flex items-start gap-3">
        <div className="bg-zinc-100 p-3 border border-zinc-300">{icon}</div>
        <div className="flex-1">
          <div className="label-overline text-zinc-400">Planned</div>
          <h3 className="font-display font-black text-xl tracking-tighter text-zinc-700 mt-1">{title}</h3>
          <p className="text-sm text-zinc-500 mt-1">{hint}</p>
        </div>
      </div>
    </div>
  );
}
